import Decimal from "decimal.js";
import { RechargeUtils } from "ebms-utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import weui from "weui.js";
import {
  Button,
  Cell,
  CellBody,
  CellFooter,
  CellHeader,
  Cells,
  CellsTips,
  CellsTitle, Input, Label
} from "../../components/weui";
import { orderService } from "../../services/orderService";
import { urls } from "../../urls";
import { formatDateTime, getAxiosError } from "../../utils";

import "./index.css";

export const Order = () => {
  const navigate = useNavigate();
  const { meterNo } = useParams();

  // 附加费计算脚本加载标记
  const [isJsLoaded, setIsJsLoaded] = useState(false);
  // 表计信息
  const [meter, setMeter] = useState(null);
  // 充值金额/数量快速选择列表
  const [vals, setVals] = useState([1000, 500, 200, 100, 50, 20]);
  // 充值金额/数量默认选中项
  const [quantity, setQuantity] = useState(0);
  // 是否为预付金额表
  const isPrepaidMoney = useMemo(() => (meter?.calMode ?? 1) === 1, [meter?.calMode]);
  // 预付类型文字描述
  const prepaidType = useMemo(() => isPrepaidMoney ? "金额" : "用量", [isPrepaidMoney]);
  // 预付值单位, 金额为元, 用量: 电表为度, 水表为吨
  const unit = useMemo(() => isPrepaidMoney ? "元" : meter?.meterClass === 1 ? "吨" : "度", [isPrepaidMoney, meter?.meterClass]);
  // 是否有附加费, 仅金额表支持附加费
  const hasSurcharge = useMemo(() => isPrepaidMoney && (meter?.surchargeMode ?? 0) !== 0, [isPrepaidMoney, meter?.surchargeMode]);
  // 计算金额
  const money = useMemo(() => {
    if (Number.isNaN(quantity)) return 0;
    // NOTE: Number.toFixed 使用 Banker's Rounding, 或者叫四舍六入, 如需使用其它舍入方式, 修改此处即可.
    // return Number(quantity * (meter?.yuFuJia ?? 0)).toFixed(2);
    Decimal.set({ rounding: Decimal.ROUND_HALF_EVEN });
    const qty = Decimal(quantity),
      price = Decimal(meter?.yuFuJia ?? 0);
    return qty.mul(price).toDP(2).toNumber(); //.toFixed(2);
  }, [quantity, meter?.yuFuJia]);
  // 计算附加费及实际下发
  const { sendMoney, surcharge } = useMemo(() => {
    if (!isJsLoaded || !meter || Number.isNaN(quantity)) return { sendMoney: 0, surcharge: 0 };
    // 用量表不支持附加费, 直接返回
    if (!isPrepaidMoney) return { sendMoney: quantity, surcharge: 0 };
    return window.surchargeCalculator(meter?.surchargeMode, meter?.surchargeType, meter?.surchargeBase, quantity);
  }, [quantity, meter, isPrepaidMoney, isJsLoaded]);

  const transactionFee = useMemo(() => {
    if (money && meter)
      return RechargeUtils.calculateTransactionFee(money, meter.transFeeRate, meter.transFeeRateUser);
  }, [money, meter]);

  const hasTransactionFee = useMemo(() => (meter?.transFeeRateUser ?? 0) > 0, [meter]);

  // 用量表充值量不是变比整数倍
  const isCTMultErr = useMemo(() => !isPrepaidMoney && sendMoney % (meter?.bianBi ?? 1) !== 0, [isPrepaidMoney, sendMoney, meter?.bianBi]);
  // 下发是否低于下限
  const hasMinBuy = useMemo(() => (meter?.minBuy ?? 0) > 0, [meter?.minBuy]);
  const overMinBuy = useMemo(() => hasMinBuy && sendMoney <= meter.minBuy, [hasMinBuy, meter?.minBuy, sendMoney]);
  // 下发是否超过上限
  const hasMaxBuy = useMemo(() => (meter?.maxBuy ?? 0) > 0, [meter?.maxBuy]);
  const overMaxBuy = useMemo(() => hasMaxBuy && sendMoney > meter.maxBuy, [hasMaxBuy, meter?.maxBuy, sendMoney]);
  // 是否可继续充值
  const canNext = useMemo(() => meter && sendMoney > 0 && !overMinBuy && !overMaxBuy && !isCTMultErr, [meter, sendMoney, overMinBuy, overMaxBuy, isCTMultErr]);

  // 充值前检查
  const check = useCallback(async () => {
    const loading = weui.loading("加载中...");
    try {
      const res = await orderService.paymentCheck(meterNo);
      const { meter, selectValIndex, selectVals } = res.data;
      setMeter(meter);
      setVals(selectVals);
      setQuantity(selectVals[selectValIndex]);
    } catch (err) {
      weui.alert(getAxiosError(err), () => {
        navigate(urls.fee());
      });
    } finally {
      loading.hide();
    }
  }, [navigate, meterNo, setMeter, setVals, setQuantity]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    // NOTE: 加载 surcharge 计算 js 文件
    const script = document.createElement("script");
    script.src = urls.surchargeJs();
    script.async = true;
    script.onload = () => setIsJsLoaded(true);
    document.body.appendChild(script);
    // 卸载时移除
    return () => { document.body.removeChild(script); };
  }, [setIsJsLoaded]);

  // 生成订单
  const handleNext = useCallback(async () => {
    if (meter?.onlineRechargeSingleLimit && meter.onlineRechargeSingleLimit > 0 && quantity > meter.onlineRechargeSingleLimit) {
      weui.alert(`超过线上单次充值上限 ${meter.onlineRechargeSingleLimit}`, { title: "订单创建失败" });
    } else {
      const loading = weui.loading("创建订单");
      try {
        const res = await orderService.add(meterNo, quantity);
        const orderNo = res.data;
        navigate(urls.rechargeChannel(orderNo));
      } catch (err) {
        weui.alert(getAxiosError(err), { title: "订单创建失败" });
      } finally {
        loading.hide();
      }
    }
  }, [meterNo, quantity, navigate, meter]);

  // render 充值金额/用量快速选择列表
  const renderQuickSelector = useCallback(() => {
    return (
      <div className="px-4 flex justify-between flex-wrap mt-4">
        {
          vals.map((v, i) => (
            <div className={ `payChoose ${v === quantity ? "active" : "payColor"}` } key={ i } onClick={ e => setQuantity(v) } onKeyDown={ e => setQuantity(v) } role="button" tabIndex={ 0 }>
              <span>{ v } { unit }</span>
            </div>
          ))
        }
      </div>
    );
  }, [vals, quantity, setQuantity, unit]);

  return (
    <>
      <CellsTitle>表计信息</CellsTitle>
      <Cells>
        <Cell>
          <CellHeader><Label>所属区域</Label></CellHeader>
          <CellBody>{ meter?.corpName }</CellBody>
        </Cell>
        <Cell>
          <CellHeader><Label>表计名称</Label></CellHeader>
          <CellBody>{ meter?.huMing }</CellBody>
        </Cell>
        <Cell>
          <CellHeader><Label>表计地址</Label></CellHeader>
          <CellBody>{ meter?.diZhi }</CellBody>
        </Cell>
        <Cell>
          <CellHeader><Label>表计编号</Label></CellHeader>
          <CellBody>{ meter?.meterId }</CellBody>
        </Cell>
        <Cell>
          <CellHeader><Label>剩余{ prepaidType }</Label></CellHeader>
          <CellBody>{ meter?.balance ?? "-" } { unit }</CellBody>
        </Cell>
        {
          !isPrepaidMoney && <Cell>
            <CellHeader><Label>单价</Label></CellHeader>
            <CellBody>{ meter?.yuFuJia ?? "-" } 元</CellBody>
          </Cell>
        }
      </Cells>
      <CellsTips>剩余{ prepaidType }采集于{ meter ? formatDateTime(new Date(meter.readingTime)) : "-" }</CellsTips>

      <CellsTitle>请选择充值{ prepaidType }</CellsTitle>
      { renderQuickSelector() }

      <Cells>
        <Cell>
          <CellHeader><Label htmlFor="elMoney">充值{ prepaidType }</Label></CellHeader>
          <CellBody>
            <Input id="elMoney" type="number" className="text-right" value={ quantity }
              onChange={ e => {
                let m = parseFloat(e.target.value);
                m = Math.round(m * Math.pow(10, 2)) / Math.pow(10, 2);
                setQuantity(m);
              } } />
          </CellBody>
          <CellFooter><span className="ml-2">{ unit }</span></CellFooter>
        </Cell>
        {
          !isPrepaidMoney &&
            <Cell>
              <CellHeader><Label htmlFor="elMoney">充值金额</Label></CellHeader>
              <CellBody>
                <div className="text-right">{ money }</div>
              </CellBody>
              <CellFooter><span className="ml-2">元</span></CellFooter>
            </Cell>
        }
        {
          hasSurcharge && <>
            <Cell>
              <CellHeader><Label>物业费</Label></CellHeader>
              <CellBody>
                <div className="text-right text-red-500">{ surcharge }</div>
              </CellBody>
              <CellFooter><span className="ml-2">{ unit }</span></CellFooter>
            </Cell>
            <Cell>
              <CellHeader><Label>实际下发</Label></CellHeader>
              <CellBody>
                <div className="text-right text-red-500">{ sendMoney }</div>
              </CellBody>
              <CellFooter><span className="ml-2">{ unit }</span></CellFooter>
            </Cell>
          </>
        }
        {
          hasTransactionFee && <>
            <Cell>
              <CellHeader><Label htmlFor="elTransFee">手续费</Label></CellHeader>
              <CellBody>
                <div className="text-right">{ transactionFee?.userFee }</div>
              </CellBody>
              <CellFooter><span className="ml-2">元</span></CellFooter>
            </Cell>
            <Cell>
              <CellHeader><Label htmlFor="elTransFee">应付金额</Label></CellHeader>
              <CellBody>
                <div className="text-right">{ transactionFee?.total }</div>
              </CellBody>
              <CellFooter><span className="ml-2">元</span></CellFooter>
            </Cell>
          </>
        }
      </Cells>
      {
        !isPrepaidMoney && <CellsTips className={ { "text-red": isCTMultErr } }>* 充值{ prepaidType }必须为 { meter?.bianBi ?? 1 } 的整数倍</CellsTips>
      }
      {
        hasMaxBuy && <CellsTips className={ { "text-red": overMaxBuy } }>* 充值上限 { meter?.maxBuy } </CellsTips>
      }
      {
        hasMinBuy > 0 && <CellsTips className={ { "text-red": overMinBuy } }>* 充值下限 { meter?.minBuy }, 原因: { meter?.minBuyReason ?? "未知" }</CellsTips>
      }

      <div className="mt-10">
        <Button type="primary" disabled={ !canNext } onClick={ handleNext }>充值</Button>
      </div>
    </>
  );
};
